/**
 * Join-failure copy map for the drop lobby.
 *
 * The drop is the losers' bracket of the AOTD race, so the join route
 * (`modules/cards/routes/publicRoutes.ts` → `cardLobbyService::joinLobby` →
 * `applyEntryPolicy`) now refuses for reasons that are about who you were in
 * today's race, not about the lobby's state. Each refusal carries a `code`
 * (`LobbyErrorCode` on the backend) alongside its message:
 *
 *  - 403 `blocked`   — an operator barred this user from today's pull.
 *  - 403 `race_only` — didn't race, so the live window isn't theirs.
 *  - 402 `entry_fee` — owes `DROP_ENTRY_FEE` and can't cover it.
 *  - 410 `drop_over` — pool drained, or the schedule's `closesAt` passed.
 *
 * Same shape as `playErrors.ts`: a code map, a tolerant reader, and a resolver
 * that degrades to the server message for anything unmapped.
 */

/**
 * Respect the winner / a non-racer pays to enter, mirroring
 * `DROP_ENTRY_FEE` in `services/respect/dropEntryPolicy.ts`. Prefer the
 * server-quoted `lobby.yourEntry.fee` wherever a snapshot is in hand; this is
 * the fallback for copy written before one arrives.
 */
export const DROP_ENTRY_FEE = 2;

export interface LobbyErrorCopy {
  title: string;
  /** A standalone sentence — the lobby toasts this on its own. */
  hint: string;
}

const KNOWN_CODE_COPY: Record<string, LobbyErrorCopy> = {
  race_only: {
    title: "The live window is for today's racers",
    hint: `You didn't race today, so the 45-second window isn't yours. You can still pull late for ${DROP_ENTRY_FEE} respect, at reduced odds, while cards last.`,
  },
  entry_fee: {
    title: "Not enough respect",
    hint: `You need ${DROP_ENTRY_FEE} respect to enter this drop. Nothing was taken.`,
  },
  blocked: {
    title: "You're blocked from this drop",
    hint: "An admin barred you from today's pull. Tomorrow is a fresh drop.",
  },
  drop_over: {
    title: "Today's drop is over",
    hint: "Every card is claimed. See you at the next one.",
  },
  participant_not_found: {
    title: "You're not in this drop",
    hint: "Join the lobby first, then pull.",
  },
};

/**
 * Pulls `{ status, data: { message?, code? } }` out of the RTK Query error
 * thrown by `useJoinLobbyMutation().unwrap()` (or a caught thrown value).
 */
function readLobbyError(error: unknown): { message?: string; code?: string } {
  if (
    error &&
    typeof error === "object" &&
    "data" in error &&
    error.data &&
    typeof error.data === "object"
  ) {
    const data = error.data as { message?: unknown; code?: unknown };
    return {
      message: typeof data.message === "string" ? data.message : undefined,
      code: typeof data.code === "string" ? data.code : undefined,
    };
  }
  return {};
}

/**
 * Maps a join failure to display copy: a known `code` wins, otherwise the
 * server message is rendered verbatim, with a generic line as the last resort.
 * `code` is passed through so a caller can react to a specific refusal (e.g.
 * refetch the snapshot after `drop_over`).
 */
export function resolveLobbyEntryError(
  error: unknown,
): LobbyErrorCopy & { code?: string } {
  const { message, code } = readLobbyError(error);

  if (code && KNOWN_CODE_COPY[code]) {
    return { ...KNOWN_CODE_COPY[code], code };
  }

  return {
    title: "Couldn't join the drop",
    hint: message || "Something went wrong. Try again in a moment.",
    code,
  };
}
